import React, { useState } from 'react';
import { Reveal } from './Reveal';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
export const HowItWorksSection = () => {
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);
  const scrollCarousel = (direction: number) => {
    const track = document.getElementById('how-it-works-track');
    if (track) {
      const card = track.querySelector(':scope > *') as HTMLElement;
      if (card) {
        const cardWidth = card.offsetWidth + 16;
        track.scrollBy({
          left: cardWidth * direction,
          behavior: 'smooth'
        });
      }
    }
  };
  const steps = [{
    number: '01',
    title: 'Diga o que você comprou',
    copy: 'Informe o nome, o tipo e quanto custou o pacote. O app calcula sozinho o custo de cada metro, cada grama, cada unidade.',
    image: 'https://cdn.magicpatterns.com/uploads/u6yrCtDSCNWEuBFiuUQsvA/tela1.png'
  }, {
    number: '02',
    title: 'Diga o que você produz',
    copy: 'Dê o nome do produto e a margem de lucro que você quer. Simples assim.',
    image: 'https://cdn.magicpatterns.com/uploads/8SGjXKWJnFSY4DNTuTTn1E/tela2.png'
  }, {
    number: '03',
    title: 'Diga do que ele é feito',
    copy: 'Marque os materiais que entram no produto e a quantidade. O preço justo aparece na hora: com lucro, com tudo.',
    image: 'https://cdn.magicpatterns.com/uploads/2RqvJTrWDtaDXnbmS2NHpN/tela3.png'
  }, {
    number: '',
    title: 'Veja seus produtos precificados',
    copy: 'Todos os seus produtos com custo, preço e lucro calculados. Tudo organizado, sem planilha.',
    image: 'https://cdn.magicpatterns.com/uploads/rLMkJJmVR9XLhNGLaqRz1W/tela4.png'
  }];
  return <>
      <section id="como-funciona" className="py-10 md:py-14 bg-rose overflow-hidden" data-id="element-246">
        <div className="max-w-[1400px] mx-auto px-[5vw]" data-id="element-247">
          <Reveal data-id="element-248">
            <div className="mb-8 md:mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6" data-id="element-249">
              <div data-id="element-250">
                <span className="font-caveat text-white text-2xl block mb-3" data-id="element-251">
                  passo a passo
                </span>
                <h2 className="font-serif font-bold text-[clamp(28px,5vw,48px)] tracking-[-0.02em] text-white" data-id="element-252">
                  Em 3 telas e 5 minutos, você sabe o{' '}
                  <span className="italic text-gold-soft" data-id="element-253">preço justo</span> de
                  qualquer produto seu.
                </h2>
              </div>

              <div className="flex gap-3" data-id="element-254">
                <button onClick={() => scrollCarousel(-1)} className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center text-white hover:bg-white hover:text-rose hover:border-white transition-all" data-id="element-255">
                  <ArrowLeft size={18} data-id="element-256" />
                </button>
                <button onClick={() => scrollCarousel(1)} className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center text-white hover:bg-white hover:text-rose hover:border-white transition-all" data-id="element-257">
                  <ArrowRight size={18} data-id="element-258" />
                </button>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.2} data-id="element-259">
            <div id="how-it-works-track" className="carousel-track pb-6" style={{
            gap: '16px',
            paddingLeft: '4px',
            paddingRight: '20px'
          }} data-id="element-260">
              {steps.map((step, i) => <div key={i} className="carousel-card w-[300px] md:w-[340px] bg-white rounded-[24px] p-5 md:p-6 shadow-card hover:-translate-y-1 transition-all" data-id="element-261">
                  {step.number && <div className="font-serif italic text-[48px] md:text-[56px] leading-none text-gold mb-3" data-id="element-262">
                      {step.number}
                    </div>}
                  <h3 className="font-serif font-bold text-lg md:text-xl text-ink mb-2" data-id="element-263">
                    {step.title}
                  </h3>
                  <p className="text-[13px] md:text-[14px] text-ink-soft mb-4 min-h-[48px]" data-id="element-264">
                    {step.copy}
                  </p>
                  <button onClick={() => setZoomedImage(step.image)} className="w-full rounded-[16px] overflow-hidden bg-bg border border-line cursor-zoom-in active:scale-[0.98] transition-transform" data-id="element-265">
                    <img src={step.image} alt={step.title} className="w-full h-auto rounded-[16px]" data-id="element-266" />
                  </button>
                </div>)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Zoom overlay */}
      <AnimatePresence data-id="element-267">
        {zoomedImage && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4 cursor-zoom-out" onClick={() => setZoomedImage(null)} data-id="element-268">
            <button onClick={() => setZoomedImage(null)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors" data-id="element-269">
              <X size={20} data-id="element-270" />
            </button>
            <motion.img initial={{
          scale: 0.8,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.8,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 25
        }} src={zoomedImage} alt="Zoom" className="max-w-full max-h-[85vh] rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-271" />
          </motion.div>}
      </AnimatePresence>
    </>;
};